// inspired by:
// https://stackoverflow.com/questions/65409862/how-to-create-a-reusable-input-field-using-react
// https://codepen.io/SnafuParadise/pen/ExpjaMV
// https://www.tailwindreact.com/application-ui/forms/input-groups

import {
  InputHTMLAttributes,
  useRef,
  forwardRef,
  ComponentProps,
  useEffect,
  memo,
} from "react";

import styles from "./index.module.css";

export type InputAttributes = InputHTMLAttributes<
  HTMLInputElement | HTMLTextAreaElement
> & {
  label?: string;
  errorText?: string;
  multiline?: boolean;
  wrapperClasses?: ComponentProps<"div">["className"];
};

const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputAttributes
>(
  (
    {
      wrapperClasses,
      label,
      className,
      id,
      name,
      value,
      errorText,
      multiline,
      ...rest
    },
    ref,
  ) => {
    const inputRef = useRef<null>(null);
    const TagName: React.ElementType = multiline ? "textarea" : "input";
    const isReadOnly = rest.readOnly && !rest.disabled;

    // Mirror the internal ref onto a forwarded one. `inputRef.current` is not
    // a valid dependency (mutating it does not re-render), and by the time an
    // effect runs after mount it is already populated.
    useEffect(() => {
      if (ref && "current" in ref) ref.current = inputRef.current;
    }, [ref]);

    return (
      <div
        className={`relative w-full ${styles.wrapper} ${wrapperClasses || ""}`}
      >
        <TagName
          ref={inputRef}
          id={id}
          name={name}
          value={value}
          placeholder=" "
          className={[
            "block w-full appearance-none rounded-md border px-3 py-3 focus:outline-none",
            styles.input,
            errorText
              ? "border-error"
              : isReadOnly
                ? "border-outline-variant focus:border-outline-variant"
                : "border-outline focus:border-on-surface focus:shadow-md",
            isReadOnly
              ? "bg-outline-variant text-on-surface cursor-not-allowed"
              : "",
            rest.disabled ? "text-outline cursor-not-allowed" : "",
            className,
          ].join(" ")}
          {...rest}
        />
        {label && (
          <label
            htmlFor={id}
            className={[
              "px-g-1 pointer-events-none absolute top-3 left-0 transition duration-200 ease-in-out",
              errorText
                ? "text-error"
                : rest.disabled
                  ? "text-outline"
                  : isReadOnly
                    ? "text-on-surface-variant"
                    : "text-on-surface",
            ].join(" ")}
          >
            {label}
          </label>
        )}
        <div className="text-error bg-surface-bright mx-3 -mt-3 min-h-5 w-fit text-sm">
          {errorText && (
            <div role="alert" className="px-g-1">
              {errorText}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default memo(Input);
